import { useCallback, useRef, useState } from 'react'
import { useScreenViewed } from '../../platform/analytics/useScreenViewed'
import { useToast } from '../../platform/feedback/ToastProvider'
import { localToday } from '../../platform/localDate'
import { useCategories } from '../categories/useCategories'
import { emitFilterChanged, emitViewed } from './budgetAnalytics'
import { isOver, monthHeader, splitActive, type BudgetFilter } from './budgetRules'
import type { Envelope } from './budgetWire'
import { BudgetForm } from './BudgetForm'
import { BudgetScreen } from './BudgetScreen'
import { useBudgets } from './useBudgets'
import { useSetBudgetActive, WRITE_COPY, BudgetWriteError } from './useBudgetWrites'

type Sheet = null | { kind: 'create' }

/**
 * `/budget`. Owns the query, the sheet and filter state and analytics; the
 * screen below it is presentational. The filter is not in the URL — no
 * requirement asks for it.
 */
export function Budget() {
  const state = useBudgets()
  const categories = useCategories()
  const toast = useToast()
  const resume = useSetBudgetActive()
  const [filter, setFilter] = useState<BudgetFilter>('all')
  const [sheet, setSheet] = useState<Sheet>(null)
  const headingRef = useRef<HTMLParagraphElement>(null)
  const today = localToday()

  const fire = useCallback(
    (items: readonly Envelope[]) => {
      const { active } = splitActive([...items])
      emitViewed(active.length, active.filter(isOver).length, monthHeader(today).remainingDays)
    },
    [today],
  )
  useScreenViewed(state, fire)

  const list = categories.status === 'success' || categories.status === 'stale' ? categories.data : null
  const active = state.status === 'success' || state.status === 'stale' ? splitActive(state.data).active : []

  return (
    <>
      <BudgetScreen
        state={state}
        categories={list}
        today={today}
        filter={filter}
        headingRef={headingRef}
        onFilter={(f) => {
          setFilter(f)
          emitFilterChanged(f)
        }}
        onCreate={() => setSheet({ kind: 'create' })}
        onResume={(e) =>
          resume.mutate(
            { id: e.id, active: true },
            {
              onError: (cause) =>
                toast.show({ kind: 'error', message: WRITE_COPY[cause instanceof BudgetWriteError ? cause.kind : 'failed'] }),
            },
          )
        }
      />
      {sheet !== null && (
        <BudgetForm
          key="new"
          mode={sheet}
          active={active}
          categories={list ?? []}
          restoreFocusTo={headingRef}
          onClose={() => setSheet(null)}
        />
      )}
    </>
  )
}
